import type { UploadToS3Params } from './media.type.ts';

// HTTP 2xx 범위. xhr.status 가 200~299 면 성공으로 간주.
const HTTP_OK = 200;
const HTTP_REDIRECT = 300;

/**
 * presigned URL로 S3에 직접 PUT.
 *
 * fetch가 아닌 XHR을 쓰는 이유: fetch는 업로드 진행률을 못 잡는다.
 * 주의: Authorization 등 커스텀 헤더를 절대 붙이지 말 것 —
 * S3는 URL 서명으로 인증하므로 서명에 없는 헤더가 끼면 403.
 */
export function uploadToS3({
  uploadUrl,
  file,
  contentType,
  onProgress,
  signal,
}: UploadToS3Params): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Upload aborted', 'AbortError'));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', contentType);

    const onAbort = () => xhr.abort();
    signal?.addEventListener('abort', onAbort);

    const cleanup = () => signal?.removeEventListener('abort', onAbort);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= HTTP_OK && xhr.status < HTTP_REDIRECT) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`[media] S3 upload failed: ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      cleanup();
      reject(new Error('[media] S3 upload network error'));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new DOMException('Upload aborted', 'AbortError'));
    };

    xhr.send(file);
  });
}
